import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const blockId = this.dataset.blockId;
        const resourceId = detail.resource?.id != null ? String(detail.resource.id) : null;
        let newProductPrice = blockId ? html.querySelector(`product-price[data-block-id="${blockId}"]`) : null;

        // Crosscheck: the block matched by id should represent the same variant that was actually
        // resolved for add-to-cart (detail.resource, the source product-form.js uses). If it doesn't,
        // trust the resolved variant over the block-id match rather than showing a mismatched price.
        if (newProductPrice && resourceId && newProductPrice.dataset.variantId && newProductPrice.dataset.variantId !== resourceId) {
          console.error('[product-price] block-id match is for a different variant than the resolved one; ignoring', {
            expectedVariantId: resourceId,
            foundVariantId: newProductPrice.dataset.variantId,
          });
          newProductPrice = null;
        }

        if (!newProductPrice) {
          // Block id can be absent or mismatched (e.g. product-card section rendering doesn't set
          // it). Fall back to matching by resolved variant id, then product id, then the only
          // candidate present, so the price doesn't silently stay stale when the block-id lookup misses.
          const productId = this.dataset.productId;
          const candidates = [...html.querySelectorAll('product-price')];
          newProductPrice =
            (resourceId && candidates.find((el) => el.dataset.variantId === resourceId)) ||
            (productId && candidates.find((el) => el.dataset.productId === productId)) ||
            (candidates.length === 1 ? candidates[0] : null) ||
            null;
        }
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
